import React, { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ar from "./ar.json";
import en from "./en.json";

const DICTIONARIES = { ar, en };
const SUPPORTED = ["ar", "en"];
const DEFAULT_LANG = "ar";

const I18nContext = createContext({
  lang: DEFAULT_LANG,
  t: (k) => k,
  switchLanguage: () => {},
  dict: DICTIONARIES[DEFAULT_LANG],
});

function resolveKey(dict, key) {
  const parts = key.split(".");
  let node = dict;
  for (const p of parts) {
    if (node && typeof node === "object" && p in node) {
      node = node[p];
    } else {
      return key;
    }
  }
  return typeof node === "string" ? node : key;
}

export function I18nProvider({ children }) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const langFromUrl = params.lang;
  const lang = SUPPORTED.includes(langFromUrl) ? langFromUrl : DEFAULT_LANG;
  const dict = DICTIONARIES[lang];

  const value = useMemo(() => {
    const t = (key) => resolveKey(dict, key);
    const switchLanguage = (nextLang) => {
      if (!SUPPORTED.includes(nextLang) || nextLang === lang) return;
      const pathname = location.pathname;
      const parts = pathname.split("/").filter(Boolean);
      if (SUPPORTED.includes(parts[0])) {
        parts[0] = nextLang;
      } else {
        parts.unshift(nextLang);
      }
      const nextPath = "/" + parts.join("/");
      // language switch preserves scroll position (see ScrollToTop).
      navigate(nextPath + location.search + location.hash, {
        state: { languageSwitch: true },
      });
    };
    return { lang, t, switchLanguage, dict, supported: SUPPORTED };
  }, [lang, dict, location, navigate]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}

export { SUPPORTED, DEFAULT_LANG };
